/**
 * Grid.js — v2
 *
 * Reescrito con CSS Grid nativo en lugar de MUI Grid.
 *
 * Mejoras sobre v1:
 *  - gap, columnGap y rowGap en píxeles reales (no theme.spacing)
 *  - Tres modos de columnas:
 *      "equal"    → N columnas de igual ancho (repeat(N, 1fr))
 *      "autofill" → columnas responsive automáticas (repeat(auto-fill, minmax(Xpx, 1fr)))
 *      "custom"   → plantilla CSS libre (ej: "1fr 2fr 1fr")
 *  - alignItems y justifyItems configurables
 *  - Sin GridItem obligatorio — los hijos se arrastran directo al grid
 *  - Presets exportados para el Toolbox
 */
import React from 'react';
import { useNode, Element } from '@craftjs/core';
import {
  TextField,
  FormControl,
  FormLabel,
  Typography,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// ─────────────────────────────────────────────────────────────────────────────
// Estilos del Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  settings: { padding: theme.spacing(2) },
  sectionLabel: {
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.08em',
    color: '#888',
    textTransform: 'uppercase',
    margin: theme.spacing(1.5, 0, 0.5),
  },
  previewRow: {
    display: 'flex',
    gap: 4,
    marginBottom: theme.spacing(1),
  },
  previewCol: {
    flex: 1,
    height: 12,
    borderRadius: 2,
    backgroundColor: '#e0e0e0',
  },
  hint: {
    fontSize: '0.7rem',
    color: '#aaa',
    marginTop: 4,
  },
  // Borde punteado solo en el editor (invisible en preview/publicado)
  gridEditor: {
    outline: '2px dashed rgba(33,150,243,0.25)',
    cursor: 'pointer',
    minHeight: 80,
    '&:hover': {
      outline: '2px dashed rgba(33,150,243,0.6)',
    },
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Presets exportados → usados en Toolbox
// ─────────────────────────────────────────────────────────────────────────────
export const GRID_PRESETS = {
  twoCol: {
    columnMode:      'equal',
    columns:         2,
    minColumnWidth:  280,
    customTemplate:  '',
    columnGap:       24,
    rowGap:          24,
    padding:         0,
    backgroundColor: 'transparent',
    alignItems:      'stretch',
    justifyItems:    'stretch',
  },
  threeCol: {
    columnMode:      'equal',
    columns:         3,
    minColumnWidth:  280,
    customTemplate:  '',
    columnGap:       24,
    rowGap:          24,
    padding:         0,
    backgroundColor: 'transparent',
    alignItems:      'stretch',
    justifyItems:    'stretch',
  },
  autoFill: {
    columnMode:      'autofill',
    columns:         3,
    minColumnWidth:  300,
    customTemplate:  '',
    columnGap:       24,
    rowGap:          24,
    padding:         0,
    backgroundColor: 'transparent',
    alignItems:      'stretch',
    justifyItems:    'stretch',
  },
  asymmetric: {
    columnMode:      'custom',
    columns:         2,
    minColumnWidth:  280,
    customTemplate:  '2fr 1fr',
    columnGap:       32,
    rowGap:          24,
    padding:         0,
    backgroundColor: 'transparent',
    alignItems:      'start',
    justifyItems:    'stretch',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper — construye el valor de grid-template-columns
// ─────────────────────────────────────────────────────────────────────────────
function buildTemplateColumns({ columnMode, columns, minColumnWidth, customTemplate }) {
  if (columnMode === 'autofill') {
    return `repeat(auto-fill, minmax(${minColumnWidth || 280}px, 1fr))`;
  }
  if (columnMode === 'custom' && customTemplate) {
    return customTemplate;
  }
  // 'equal' (default)
  return `repeat(${columns || 2}, 1fr)`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente Grid
// ─────────────────────────────────────────────────────────────────────────────
export const Grid = ({
  children,
  columnMode,
  columns,
  minColumnWidth,
  customTemplate,
  columnGap,
  rowGap,
  padding,
  backgroundColor,
  alignItems,
  justifyItems,
}) => {
  const classes = useStyles();
  const { connectors: { connect, drag } } = useNode();

  const gridStyle = {
    display:             'grid',
    gridTemplateColumns: buildTemplateColumns({ columnMode, columns, minColumnWidth, customTemplate }),
    columnGap:           `${columnGap ?? 24}px`,
    rowGap:              `${rowGap ?? 24}px`,
    padding:             padding ? `${padding}px` : undefined,
    backgroundColor:     backgroundColor && backgroundColor !== 'transparent'
                           ? backgroundColor
                           : undefined,
    alignItems:          alignItems  || 'stretch',
    justifyItems:        justifyItems || 'stretch',
    width:               '100%',
    boxSizing:           'border-box',
  };

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      className={classes.gridEditor}
      style={gridStyle}
    >
      {children}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const GridSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    columnMode,
    columns,
    minColumnWidth,
    customTemplate,
    columnGap,
    rowGap,
    padding,
    backgroundColor,
    alignItems,
    justifyItems,
  } = useNode((node) => ({ ...node.data.props }));

  const templatePreview = buildTemplateColumns({ columnMode, columns, minColumnWidth, customTemplate });

  return (
    <div className={classes.settings}>

      {/* ── COLUMNAS ────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Columnas</Typography>

      {/* Preview visual de columnas */}
      <div className={classes.previewRow}>
        {columnMode === 'equal' && Array.from({ length: columns || 2 }).map((_, i) => (
          <div key={i} className={classes.previewCol} />
        ))}
        {columnMode === 'autofill' && [1, 2, 3].map((i) => (
          <div key={i} className={classes.previewCol} />
        ))}
        {columnMode === 'custom' && [1, 2].map((i) => (
          <div key={i} className={classes.previewCol} style={{ flex: i === 1 ? 2 : 1 }} />
        ))}
      </div>

      <FormControl fullWidth margin="dense">
        <FormLabel>Modo de columnas</FormLabel>
        <TextField
          select
          size="small"
          value={columnMode || 'equal'}
          onChange={(e) => setProp((p) => (p.columnMode = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="equal">Igual ancho (N columnas)</option>
          <option value="autofill">Auto-fill responsive</option>
          <option value="custom">Plantilla personalizada</option>
        </TextField>
      </FormControl>

      {(columnMode === 'equal' || !columnMode) && (
        <FormControl fullWidth margin="dense">
          <FormLabel>Número de columnas</FormLabel>
          <TextField
            type="number"
            size="small"
            inputProps={{ min: 1, max: 12 }}
            value={columns ?? 3}
            onChange={(e) => setProp((p) => (p.columns = parseInt(e.target.value)))}
            helperText="1 a 12 columnas de igual ancho"
          />
        </FormControl>
      )}

      {columnMode === 'autofill' && (
        <FormControl fullWidth margin="dense">
          <FormLabel>Ancho mínimo por columna (px)</FormLabel>
          <TextField
            type="number"
            size="small"
            inputProps={{ min: 100, max: 800 }}
            value={minColumnWidth ?? 280}
            onChange={(e) => setProp((p) => (p.minColumnWidth = parseInt(e.target.value)))}
            helperText="Las columnas se crean automáticamente según el espacio disponible"
          />
        </FormControl>
      )}

      {columnMode === 'custom' && (
        <FormControl fullWidth margin="dense">
          <FormLabel>Plantilla de columnas (CSS)</FormLabel>
          <TextField
            size="small"
            value={customTemplate || ''}
            onChange={(e) => setProp((p) => (p.customTemplate = e.target.value))}
            placeholder="Ej: 1fr 2fr 1fr"
            helperText="Usa fracciones (fr), px o %. Ej: 2fr 1fr"
          />
          <Typography className={classes.hint}>
            Resultado: {templatePreview}
          </Typography>
        </FormControl>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── ESPACIADO ───────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Espaciado</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Espacio entre columnas (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 0, max: 120 }}
          value={columnGap ?? 24}
          onChange={(e) => setProp((p) => (p.columnGap = parseInt(e.target.value)))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Espacio entre filas (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 0, max: 120 }}
          value={rowGap ?? 24}
          onChange={(e) => setProp((p) => (p.rowGap = parseInt(e.target.value)))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Padding del grid (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 0, max: 120 }}
          value={padding ?? 0}
          onChange={(e) => setProp((p) => (p.padding = parseInt(e.target.value)))}
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── ALINEACIÓN ──────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Alineación</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Alinear ítems (vertical)</FormLabel>
        <TextField
          select
          size="small"
          value={alignItems || 'stretch'}
          onChange={(e) => setProp((p) => (p.alignItems = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="stretch">Estirar (stretch)</option>
          <option value="start">Inicio (start)</option>
          <option value="center">Centro (center)</option>
          <option value="end">Final (end)</option>
        </TextField>
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Alinear ítems (horizontal)</FormLabel>
        <TextField
          select
          size="small"
          value={justifyItems || 'stretch'}
          onChange={(e) => setProp((p) => (p.justifyItems = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="stretch">Estirar (stretch)</option>
          <option value="start">Inicio (start)</option>
          <option value="center">Centro (center)</option>
          <option value="end">Final (end)</option>
        </TextField>
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── FONDO ───────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Fondo</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color de fondo</FormLabel>
        <TextField
          type="color"
          size="small"
          value={
            backgroundColor && backgroundColor !== 'transparent' && backgroundColor.startsWith('#')
              ? backgroundColor
              : '#ffffff'
          }
          onChange={(e) => setProp((p) => (p.backgroundColor = e.target.value))}
        />
      </FormControl>

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// craft config
// ─────────────────────────────────────────────────────────────────────────────
Grid.craft = {
  displayName: 'Grid',
  isCanvas: true,
  props: { ...GRID_PRESETS.threeCol },
  related: { settings: GridSettings },
};